import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { Bounds, Environment, OrbitControls } from '@react-three/drei';
import { useAppStore } from '@/state/store';
import { SceneLighting } from './SceneLighting';
import { BatMesh } from './BatMesh';
import { ProfileOverlayLine } from './ProfileOverlayLine';
import { GroundShadow } from './GroundShadow';
import { formatLength } from '@/lib/utils/units';

function EmptyState() {
  return (
    <div className="empty-state">
      <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M4 12c1.5-4 4-6 8-6s6.5 2 8 6c-1.5 4-4 6-8 6s-6.5-2-8-6Z"
          stroke="currentColor"
          strokeWidth="1.4"
        />
        <circle cx="12" cy="12" r="2.2" stroke="currentColor" strokeWidth="1.4" />
      </svg>
      <div className="empty-state-title">No model loaded</div>
      <div className="empty-state-subtitle">
        Drag and drop a Mark Lumber .C profile file into the sidebar to generate a 3D bat.
      </div>
    </div>
  );
}

function ViewerToolbar() {
  const viewer = useAppStore((s) => s.viewer);
  const setViewerOption = useAppStore((s) => s.setViewerOption);
  const hasModel = useAppStore((s) => !!s.model);

  if (!hasModel) return null;

  return (
    <div className="viewer-toolbar">
      <label className={`viewer-toggle${viewer.wireframe ? ' is-active' : ''}`}>
        <input
          type="checkbox"
          checked={viewer.wireframe}
          onChange={(e) => setViewerOption('wireframe', e.target.checked)}
        />
        Wireframe
      </label>
      <label className={`viewer-toggle${viewer.profileOverlay ? ' is-active' : ''}`}>
        <input
          type="checkbox"
          checked={viewer.profileOverlay}
          onChange={(e) => setViewerOption('profileOverlay', e.target.checked)}
        />
        Profile Overlay
      </label>
      <label className={`viewer-toggle${viewer.sectionClipping ? ' is-active' : ''}`}>
        <input
          type="checkbox"
          checked={viewer.sectionClipping}
          onChange={(e) => setViewerOption('sectionClipping', e.target.checked)}
        />
        Section Clip
      </label>
      {viewer.sectionClipping && (
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={viewer.clipPosition}
          onChange={(e) => setViewerOption('clipPosition', Number(e.target.value))}
          style={{ accentColor: 'var(--steel)' }}
        />
      )}
    </div>
  );
}

function ViewerBadge() {
  const model = useAppStore((s) => s.model);
  const displayUnit = useAppStore((s) => s.viewer.displayUnit);
  if (!model) return null;
  const filledCount = model.trim.gaps.filter((g) => g.filled).length;
  const unresolvedCount = model.trim.gaps.filter((g) => !g.filled).length;
  return (
    <div className="viewer-badge">
      {model.modelName} · {formatLength(model.metrics.length, displayUnit)} ·{' '}
      {model.metrics.totalProfilePoints} pts
      {filledCount > 0 && <span style={{ color: 'var(--steel)' }}> · {filledCount} reconstructed</span>}
      {unresolvedCount > 0 && <span style={{ color: 'var(--danger)' }}> · {unresolvedCount} gap{unresolvedCount === 1 ? '' : 's'}</span>}
    </div>
  );
}

export function Viewer3D() {
  const geometry = useAppStore((s) => s.geometry);
  const setCanvasRef = useAppStore((s) => s.setCanvasRef);

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-app)' }}>
      <Canvas
        shadows
        gl={{ preserveDrawingBuffer: true, antialias: true }}
        camera={{ fov: 40, near: 1, far: 5000, position: [400, 200, 400] }}
        onCreated={({ gl }) => setCanvasRef(gl.domElement)}
      >
        <color attach="background" args={['#0a0c0e']} />
        <SceneLighting />
        <Suspense fallback={null}>
          <Environment preset="warehouse" background={false} />
        </Suspense>

        {geometry && (
          <Bounds fit clip observe margin={1.35}>
            <BatMesh />
            <ProfileOverlayLine />
          </Bounds>
        )}

        {geometry && <GroundShadow />}

        <OrbitControls makeDefault enableDamping dampingFactor={0.08} minDistance={20} maxDistance={3000} />
      </Canvas>

      <ViewerToolbar />
      <ViewerBadge />
      {!geometry && <EmptyState />}
    </div>
  );
}
